import { useNavigate } from "react-router-dom";
import PageHeading from "../../Components/PageHeading/PageHeading";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import "./MarketingPosterAI.css";
import AiFactsDisplay from "../../Components/AiFacts/AiFactsDisplay";
import SamplePdfModal from "../../Components/SamplePdfModal/SamplePdfModal";
import ContentMarketingPoster from "../Gallery/ContentMarketingPoster";

const primaryColorOptions = [
  { value: "deep-navy-blue", label: "Deep Navy Blue" },
  { value: "rich-indigo", label: "Rich Indigo" },
  { value: "dark-forest-green", label: "Dark Forest Green" },
  { value: "dark-maroon-burgundy", label: "Dark Maroon / Burgundy" },
  { value: "deep-teal", label: "Deep Teal" },
  { value: "dark-espresso-brown", label: "Dark Espresso Brown" },
  { value: "true-charcoal-black", label: "True Charcoal Black" },
  { value: "indigo-violet", label: "Indigo-Violet" },
  { value: "darker-imperial-purple", label: "Darker Imperial Purple" },
  { value: "deep-olive-drab", label: "Deep Olive Drab" },
  { value: "midnight-arctic-blue", label: "Midnight Arctic Blue" },
  { value: "very-dark-aubergine", label: "Very Dark Aubergine" },
  { value: "dark-hunter-green", label: "Dark Hunter Green" },
  { value: "dark-crimson", label: "Dark Crimson" },
];

const specificSecondaryColors = [
  { value: "white", label: "White" },
  { value: "vibrant-gold", label: "Vibrant Gold" },
  { value: "gold", label: "Gold" },
  { value: "soft-mint", label: "Soft Mint" },
  { value: "bright-amber", label: "Bright Amber" },
  { value: "azure-mist", label: "Azure Mist" },
  { value: "creamed-honey", label: "Creamed Honey" },
  { value: "medium-gray", label: "Medium Gray" },
  { value: "lavender-mist", label: "Lavender Mist" },
  { value: "amber", label: "Amber" },
  { value: "wheat", label: "Wheat" },
  { value: "light-blue", label: "Light Blue" },
  { value: "goldenrod", label: "Goldenrod" },
  { value: "light-green", label: "Light Green" },
  { value: "lemon-chiffon", label: "Lemon Chiffon" },
];

const secondaryColorOptions = [
  { value: "random", label: "Random" },
  ...specificSecondaryColors,
];

const accentColorOptions = [
  { value: "random", label: "Random" },
  ...specificSecondaryColors,
];

const aspectRatioOptions = [
  { value: "1:1", label: "1:1", description: "Square poster" },
  { value: "9:16", label: "9:16", description: "Mobile / Story / TikTok" },
  { value: "16:9", label: "16:9", description: "Wide cinematic poster" },
];

const getRandomPrimaryColor = () => {
  const randomIndex = Math.floor(Math.random() * primaryColorOptions.length);
  return primaryColorOptions[randomIndex].value;
};

const getRandomSecondaryColor = (excludeColor = null) => {
  const availableColors = specificSecondaryColors.filter(
    (option) => option.value !== excludeColor
  );
  if (availableColors.length === 0) return specificSecondaryColors[0].value;
  const randomIndex = Math.floor(Math.random() * availableColors.length);
  return availableColors[randomIndex].value;
};

const MAX_IMAGES = 4;
const baseCredit = 75;

const MarketingPosterAI = () => {
  const [texts, setTexts] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [brandName, setBrandName] = useState("");
  const [logoImage, setLogoImage] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [primaryColor, setPrimaryColor] = useState(() =>
    getRandomPrimaryColor()
  );
  const [colors, setColors] = useState(() => {
    const s = getRandomSecondaryColor();
    const a = getRandomSecondaryColor(s);
    return { secondaryColor: s, accentColor: a };
  });
  const [aspectRatio, setAspectRatio] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [suggestedPrompt, setSuggestedPrompt] = useState("");
  const [checkingPrompt, setCheckingPrompt] = useState(false);
  const promptCheckTimeout = useRef(null);

  const [customPrimaryColor, setCustomPrimaryColor] = useState("#000000");
  const [customSecondaryColor, setCustomSecondaryColor] = useState("#ffffff");
  const [customAccentColor, setCustomAccentColor] = useState("#ffffff");

  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const secondaryColor = colors.secondaryColor;
  const accentColor = colors.accentColor;

  const handleSecondaryColorChange = (val) => {
    setColors((prev) => {
      const nextAccent =
        val !== "random" && val !== "custom" && prev.accentColor === val ? "random" : prev.accentColor;
      return { secondaryColor: val, accentColor: nextAccent };
    });
  };

  const handleAccentColorChange = (val) => {
    setColors((prev) => ({ ...prev, accentColor: val }));
  };

  const { AIuser, userRefetch, setUserRefetch } = useContext(UserAuthContext);
  const navigate = useNavigate();

  const creditToLocalString = AIuser?.credits
    ? parseInt(Math.floor(AIuser?.credits)).toLocaleString()
    : "0";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = MAX_IMAGES - productImages.length;

    if (files.length > remainingSlots) {
      Swal.fire({
        text: `You can upload a maximum of ${MAX_IMAGES} images. You have ${remainingSlots} slot(s) remaining.`,
        icon: "warning",
        confirmButtonText: "Ok",
        customClass: { popup: "modal_class_success" },
      });
    }

    const filesToAdd = files.slice(0, remainingSlots);
    if (filesToAdd.length === 0) return;

    const newImages = [...productImages, ...filesToAdd];
    setProductImages(newImages);

    filesToAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    // Reset file input so user can select same file again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = (e, index) => {
    e.stopPropagation();
    setProductImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleRemoveLogo = (e) => {
    e.stopPropagation();
    setLogoImage(null);
    setLogoPreview(null);
  };

  const checkPromptSuggestion = async (value) => {
    if (!value?.trim()) {
      setSuggestedPrompt("");
      return;
    }

    try {
      setCheckingPrompt(true);

      const res = await axios.post(
        "https://backend.whalesai.com/api/v1/prompt-checker/check",
        {
          prompt: value,
          aiService: "marketing-poster-ai",
        },
        {
          headers: {
            authorization: `Bearer ${localStorage.getItem("aiUser")}`,
          },
        }
      );

      if (res?.data?.success) {
        setSuggestedPrompt(res.data.data.suggested_prompt);
      }
    } catch (error) {
      console.log("Prompt checker error:", error);
    } finally {
      setCheckingPrompt(false);
    }
  };

  // const handleSubmitCredits = async () => {
  //   const deductedCredits = AIuser.credits - baseCredit;
  //   await axios
  //     .put(
  //       "https://backend.whalesai.com/api/dashboard/user_signup-signin/update-credits",
  //       { email: AIuser?.email, credits: deductedCredits },
  //       {
  //         headers: {
  //           authorization: `Bearer ${localStorage.getItem("aiUser")}`,
  //         },
  //       }
  //     )
  //     .then((res) => {
  //       if (res.status === 200) {
  //         setUserRefetch(!userRefetch);
  //       }
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // };

  const handleGenerate = async () => {
    if (AIuser.credits < baseCredit) {
      Swal.fire({
        text: "You don't have enough credits! Please buy credits to continue.",
        icon: "error",
        confirmButtonText: "Buy",
        customClass: {
          popup: "modal_class_success",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/purchase");
        }
      });
      return;
    }

    if (!texts.trim()) {
      Swal.fire({
        text: "Please enter texts.",
        icon: "warning",
        confirmButtonText: "Ok",
        customClass: { popup: "modal_class_success" },
      });
      return;
    }

    if (!aspectRatio) {
      Swal.fire({
        text: "Please select an aspect ratio.",
        icon: "warning",
        confirmButtonText: "Ok",
        customClass: { popup: "modal_class_success" },
      });
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("credit", baseCredit);
      formData.append("texts", texts);
      formData.append("ctaText", ctaText);
      formData.append("brandName", brandName);
      formData.append("primaryColor", primaryColor === "custom" ? customPrimaryColor : primaryColor);
      formData.append("secondaryColor", secondaryColor === "custom" ? customSecondaryColor : secondaryColor);
      formData.append("accentColor", accentColor === "custom" ? customAccentColor : accentColor);
      formData.append("aspectRatio", aspectRatio);

      if (logoImage) {
        formData.append("logo", logoImage);
      }

      productImages.forEach((file) => {
        formData.append("images", file);
      });

      const response = await fetch(
        "https://backend.whalesai.com/api/v1/marketing-poster-ai/generate",
        {
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("aiUser")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate Marketing Poster");
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Generation failed");
      }

      // await handleSubmitCredits();
      setUserRefetch(!userRefetch)
      setResultImage(data?.data?.imageUrl);

      // Reset form
      setTexts("");
      setCtaText("");
      setBrandName("");
      setLogoImage(null);
      setLogoPreview(null);
      setProductImages([]);
      setImagePreviews([]);
      setAspectRatio("");
      setPrimaryColor(getRandomPrimaryColor());
      const newS = getRandomSecondaryColor();
      const newA = getRandomSecondaryColor(newS);
      setColors({ secondaryColor: newS, accentColor: newA });
      setCustomPrimaryColor("#000000");
      setCustomSecondaryColor("#ffffff");
      setCustomAccentColor("#ffffff");
      setSuggestedPrompt("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await Swal.fire({
        html: `
    <p class="text-white">
      Your marketing poster has been generated successfully<br/>
      You can view and download it below.
    </p>
  `,
        icon: "success",
        confirmButtonText: "Close",
        customClass: { popup: "modal_class_success" },
      });
    } catch (error) {
      console.error("Error generating Marketing Poster:", error);

      Swal.fire({
        text: error.message || "Something went wrong!",
        icon: "error",
        confirmButtonText: "Ok",
        customClass: {
          popup: "modal_class_success",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async ({ imageUrl }) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const fileName = imageUrl.split("/").pop();
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Image download failed:", error);
    }
  };

  const handleClear = () => {
    setTexts("");
    setCtaText("");
    setBrandName("");
    setLogoImage(null);
    setLogoPreview(null);
    setProductImages([]);
    setImagePreviews([]);
    setAspectRatio("");
    setPrimaryColor(getRandomPrimaryColor());
    const newS = getRandomSecondaryColor();
    const newA = getRandomSecondaryColor(newS);
    setColors({ secondaryColor: newS, accentColor: newA });
    setCustomPrimaryColor("#000000");
    setCustomSecondaryColor("#ffffff");
    setCustomAccentColor("#ffffff");
    setResultImage(null);
    setSuggestedPrompt("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const selectStyle = {
    background: "#011C34",
    border: "2px solid #FFF",
    borderRadius: "12px",
    outline: "none",
    cursor: "pointer",
    appearance: "auto",
  };

  const isFormValid =
    texts.trim() &&
    aspectRatio;

  return (
    <Fragment>
      {/* <PageHeading
        title={"PROMO CAMPAIGN AI"}
        description={
          "Create stunning marketing posters with AI in seconds."
        }
        onInfoClick={() => setShowPdfModal(true)}
        infoTooltip="See sample marketing poster"
      /> */}

      <SamplePdfModal pdfModal={showPdfModal} setPdfModal={setShowPdfModal} title="What you can do?">
        <ContentMarketingPoster />
      </SamplePdfModal>

      <div
        className="container pb-2"
        style={{
          maxWidth: "872px",
          paddingInline: "12px",
          marginInline: "auto",
        }}
      >
        <div className="container">
          {/* Credits section */}
          <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2">
            <div>
              {/* <div>
                <button className="credits-button">
                  CREDITS : {creditToLocalString}
                </button>
                <button
                  className="buy-credits-button"
                  onClick={() => navigate("/purchase")}
                >
                  BUY CREDITS
                </button>
                <button
                  onClick={() => navigate("/usage")}
                  className="money-exchange-button"
                >
                  <img src={moneyExchangeIcon} alt="usage" />
                </button>
              </div> */}
              <p className="all-page-title all-page-gradient-text text-center my-4">PROMO CAMPAIGN AI</p>
              <div className="credits-actions">

                <button className="credits-button">
                  CREDITS : {creditToLocalString}
                </button>

                <button
                  className="buy-credits-button"
                  onClick={() => navigate("/purchase")}
                >
                  BUY CREDITS
                </button>

                <button
                  onClick={() => navigate("/usage")}
                  className="money-exchange-button"
                >
                  <img src={moneyExchangeIcon} alt="usage" />
                </button>

                <button
                  type="button"
                  className="money-exchange-button"
                  onClick={() => {
                    setShowPdfModal(true);
                  }}

                >
                  <img src={sampleIcon} alt="See sample video" />
                </button>

              </div>
              <p
                style={{ fontSize: "8px" }}
                className="text-white mt-2 creditText text-center"
              >
                {baseCredit} Credits (USD 0.90 based on Credits Package 3)
              </p>
            </div>
          </div>

          {/* Step 1: Upload Images */}
          <div className="mb-4 mt-4">
            <div className="marketing-poster-step-label">Step 1</div>
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Upload Images (up to {MAX_IMAGES}) <span style={{ fontSize: "12px", opacity: 0.7 }}>(Optional)</span>
            </label>
            <div
              className={`marketing-poster-upload-area ${imagePreviews.length > 0 ? "has-images" : ""}`}
              onClick={() => {
                if (productImages.length < MAX_IMAGES) {
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                multiple
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
              {imagePreviews.length > 0 ? (
                <div className="marketing-poster-preview-grid">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="marketing-poster-preview-item">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="marketing-poster-preview-img"
                      />
                      <button
                        className="marketing-poster-remove-btn"
                        onClick={(e) => handleRemoveImage(e, index)}
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {productImages.length < MAX_IMAGES && (
                    <div
                      className="marketing-poster-add-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      <div className="marketing-poster-add-icon">+</div>
                      <div className="marketing-poster-add-text">Add More</div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="marketing-poster-upload-icon">📷</div>
                  <div className="marketing-poster-upload-text">
                    Drag & drop your images here or <span>browse</span>
                  </div>
                  <div
                    className="marketing-poster-upload-text mt-1"
                    style={{ fontSize: "12px" }}
                  >
                    Supports JPG, PNG, WEBP — up to {MAX_IMAGES} images
                  </div>
                </>
              )}
            </div>
            <p className="text-white mt-1" style={{ fontSize: "11px", opacity: 0.7 }}>
              {productImages.length}/{MAX_IMAGES} images selected
            </p>
          </div>

          <div className="mb-4">
            <div className="marketing-poster-step-label">Step 2</div>
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Brand Name <span style={{ fontSize: "12px", opacity: 0.7 }}>(Optional)</span>
            </label>
            <input
              type="text"
              className="p-2 px-3 w-100 text-white"
              style={selectStyle}
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="Enter brand name..."
            />
          </div>

          <div className="mb-4">
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Brand Logo <span style={{ fontSize: "12px", opacity: 0.7 }}>(Optional)</span>
            </label>
            <div
              className={`marketing-poster-upload-area ${logoPreview ? "has-images" : ""}`}
              onClick={() => {
                if (!logoImage) {
                  logoInputRef.current?.click();
                }
              }}
              style={{ minHeight: "120px" }}
            >
              <input
                type="file"
                ref={logoInputRef}
                accept="image/*"
                onChange={handleLogoChange}
                style={{ display: "none" }}
              />
              {logoPreview ? (
                <div className="marketing-poster-preview-grid">
                  <div className="marketing-poster-preview-item">
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      className="marketing-poster-preview-img"
                    />
                    <button
                      className="marketing-poster-remove-btn"
                      onClick={handleRemoveLogo}
                      title="Remove logo"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="marketing-poster-upload-icon">📷</div>
                  <div className="marketing-poster-upload-text">
                    Drag & drop your logo here or <span>browse</span>
                  </div>
                  <div
                    className="marketing-poster-upload-text mt-1"
                    style={{ fontSize: "12px" }}
                  >
                    Supports JPG, PNG, WEBP — 1 image
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Step 2: Texts */}
          <div className="mb-4">
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Texts <span className="text-danger">*</span>
            </label>
            <textarea
              className="p-2 px-3 w-100 text-white"
              style={{
                background: "#011C34",
                border: "2px solid #FFF",
                borderRadius: "12px",
                outline: "none",
                height: "80px",
                resize: "none",
              }}
              value={texts}
              onChange={(e) => {
                const value = e.target.value;
                setTexts(value);

                if (promptCheckTimeout.current) {
                  clearTimeout(promptCheckTimeout.current);
                }

                promptCheckTimeout.current = setTimeout(() => {
                  checkPromptSuggestion(value);
                }, 1500);
              }}
              placeholder="Enter texts..."
            />
            {checkingPrompt && (
              <div className="d-flex align-items-center mt-1 mb-2">
                <div className="spinner-border text-light spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="text-warning" style={{ marginLeft: "8px" }}>Generating suggestion...</span>
              </div>
            )}

            {suggestedPrompt && (
              <div
                onClick={() => setTexts(suggestedPrompt)}
                style={{
                  cursor: "pointer",
                  background: "#0b2d4d",
                  padding: "12px",
                  borderRadius: "10px",
                  marginTop: "8px",
                  border: "1px solid #fff",
                }}
              >
                <p className="mb-1 text-warning">Suggested Prompt:</p>
                <p className="mb-0 text-white">{suggestedPrompt}</p>
              </div>
            )}
          </div>

          {/* Step 3: CTA Text & Brand Name */}
          <div className="mb-4">
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              CTA Text <span style={{ fontSize: "12px", opacity: 0.7 }}>(Optional)</span>
            </label>
            <input
              type="text"
              className="p-2 px-3 w-100 text-white"
              style={selectStyle}
              value={ctaText}
              onChange={(e) => setCtaText(e.target.value)}
              placeholder="e.g., Shop Now, Learn More..."
            />
          </div>

          {/* Step 4: Color Options */}
          <div className="mb-4">
            <div className="d-flex flex-column flex-md-row gap-3">
              <div className="flex-fill">
                <label
                  className="text-white mb-2"
                  style={{ fontSize: "14px" }}
                >
                  Primary Color <span style={{ fontSize: "12px", opacity: 0.7 }}>(Optional)</span>
                </label>
                <select
                  className="p-2 px-3 w-100 text-white"
                  style={selectStyle}
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                >
                  <option value="custom" style={{ color: '#ffa21f', fontWeight: 'bold' }}>Custom Color</option>
                  <option value="random">Random</option>
                  {primaryColorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {primaryColor === "custom" && (
                  <div className="mt-2 text-center">
                    <input
                      type="color"
                      value={customPrimaryColor}
                      onChange={(e) => setCustomPrimaryColor(e.target.value)}
                      style={{ width: "100%", height: "40px", cursor: "pointer", border: "none", background: "transparent", padding: 0 }}
                    />
                    <div className="text-white mt-1" style={{ fontSize: "12px", fontFamily: "monospace" }}>
                      {customPrimaryColor.toUpperCase()}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-fill">
                <label
                  className="text-white mb-2"
                  style={{ fontSize: "14px" }}
                >
                  Secondary Color <span style={{ fontSize: "12px", opacity: 0.7 }}>(Optional)</span>
                </label>
                <select
                  className="p-2 px-3 w-100 text-white"
                  style={selectStyle}
                  value={secondaryColor}
                  onChange={(e) => handleSecondaryColorChange(e.target.value)}
                >
                  <option value="custom" style={{ color: '#ffa21f', fontWeight: 'bold' }}>Custom Color</option>
                  {secondaryColorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {secondaryColor === "custom" && (
                  <div className="mt-2 text-center">
                    <input
                      type="color"
                      value={customSecondaryColor}
                      onChange={(e) => setCustomSecondaryColor(e.target.value)}
                      style={{ width: "100%", height: "40px", cursor: "pointer", border: "none", background: "transparent", padding: 0 }}
                    />
                    <div className="text-white mt-1" style={{ fontSize: "12px", fontFamily: "monospace" }}>
                      {customSecondaryColor.toUpperCase()}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-fill">
                <label
                  className="text-white mb-2"
                  style={{ fontSize: "14px" }}
                >
                  Accent Color <span style={{ fontSize: "12px", opacity: 0.7 }}>(Optional)</span>
                </label>
                <select
                  className="p-2 px-3 w-100 text-white"
                  style={selectStyle}
                  value={accentColor}
                  onChange={(e) => handleAccentColorChange(e.target.value)}
                >
                  <option value="custom" style={{ color: '#ffa21f', fontWeight: 'bold' }}>Custom Color</option>
                  {accentColorOptions
                    .filter(
                      (option) =>
                        option.value === "random" ||
                        option.value === "custom" ||
                        option.value !== secondaryColor
                    )
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
                {accentColor === "custom" && (
                  <div className="mt-2 text-center">
                    <input
                      type="color"
                      value={customAccentColor}
                      onChange={(e) => setCustomAccentColor(e.target.value)}
                      style={{ width: "100%", height: "40px", cursor: "pointer", border: "none", background: "transparent", padding: 0 }}
                    />
                    <div className="text-white mt-1" style={{ fontSize: "12px", fontFamily: "monospace" }}>
                      {customAccentColor.toUpperCase()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Step 5: Aspect Ratio */}
          <div className="mb-4">
            <div className="marketing-poster-step-label">Step 3</div>
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Aspect Ratio <span className="text-danger">*</span>
            </label>
            <div className="marketing-poster-ratio-grid">
              {aspectRatioOptions.map((option) => (
                <div
                  key={option.value}
                  className={`marketing-poster-ratio-card ${aspectRatio === option.value ? "selected" : ""
                    }`}
                  onClick={() => setAspectRatio(option.value)}
                >
                  <div className="ratio-value">{option.label}</div>
                  <div className="ratio-label">{option.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-center align-items-center mb-5">
            <Button
              sx={{
                padding: "8px 30px",
                marginRight: "10px",
                color: "#000",
                background: "#fff",
                "&:hover": { background: "#fff" },
                "&:disabled": { background: "#fff" },
              }}
              onClick={handleClear}
              disabled={
                (!texts &&
                  !ctaText &&
                  !brandName &&
                  !aspectRatio &&
                  productImages.length === 0 &&
                  !logoImage) ||
                isLoading
              }
              variant="contained"
            >
              Clear
            </Button>
            <Button
              sx={{
                padding: "8px 30px",
                "&:disabled": { background: "gray" },
              }}
              onClick={handleGenerate}
              className="generate-button text-white"
              disabled={!isFormValid || isLoading}
              variant="contained"
            >
              {isLoading ? "Processing..." : "Generate"}
            </Button>
          </div>

          <div className="animate text-md-center text-lg-center text-center mb-5 d-flex justify-content-center force-small-font">
            Please do not navigate away from the browser.
            <br /> Doing so will stop the process.
          </div>
          <div className="text-md-center text-white text-lg-center text-center d-flex justify-content-center force-small-font mt-1">
            While AI strives for accuracy, it may occasionally make mistakes. <br />Please verify any important information.
          </div>
          <div className="d-flex justify-content-center mt-3">
            {isLoading && (
              <div
                className="spinner-border text-light"
                role="status"
                style={{ width: "4rem", height: "4rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
          </div>

          {/* Interesting facts while generating */}
          {isLoading && <AiFactsDisplay />}

          {resultImage && (
            <div className="mt-3 d-flex flex-column align-items-center justify-content-center">
              <p className="text-white">Generated Image</p>
              <img src={resultImage} alt="Generated" className="img-fluid" />
            </div>
          )}

          {resultImage && (
            <div>
              <button
                className="btn btn-success mt-3"
                onClick={() => {
                  handleDownload({ imageUrl: resultImage });
                }}
              >
                <i className="fa-solid fa-download"></i> Download
              </button>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default MarketingPosterAI;